import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';

interface ServiceItem {
  title: string;
  description: string;
}

interface AccordionSection {
  id: string;
  title: string;
  items: ServiceItem[];
}

const servicesData: AccordionSection[] = [
  {
    id: 'engage',
    title: 'Engage Through Branding',
    items: [
      {
        title: 'Copyediting and Proofreading',
        description: 'Writing a book never comes easy. Whether you are a new or published author, getting your work professionally edited is one important factor. Leavitt Peak Press\' editorial service helps you enhance your book\'s readability, clarity, and quality',
      },
      {
        title: 'Design',
        description: '"First impression last". The moment we choose a book we always consider its cover. Our design team will help your book stand out, its cover design, interior layout and illustration.',
      },
      {
        title: 'Digital Marketing',
        description: 'Understanding the business objectives and identifying the target audience relevant to book publishing can be a potent strategy to adopt. An author with a strong presence on social media not only enhances credibility but also draws in the intended audience. Leveraging a team with diverse experiences in social media marketing, we possess the expertise to elevate your reach to where your potential readers reside.\n\nIndeed! Our aim is for your work to traverse the globe in mere minutes, allowing it to make a profound impact.',
      },
    ],
  },
  {
    id: 'marketplace',
    title: 'Establish In Marketplace',
    items: [
      {
        title: 'Radio Interview',
        description: 'There\'s an endless array of venues for discussing books. From cafes and libraries to bookstores and radio shows, the options are plentiful. As an author, the airwaves offer not just a platform to share your passion, but also an avenue to effectively promote your book to engaged listeners. Leavitt Peak Press offers a variety of radio platforms tailored to different genres.',
      },
      {
        title: 'Design',
        description: 'As an accomplished author, the most effective method to announce your success to the world is by crafting a vibrant and captivating marketing platform, utilizing Leavitt Peak Press\'s video advertising services. Witness your words spring to life through engaging motion graphics and expert voiceover.',
      },
      {
        title: 'Press Release',
        description: 'Understanding the business objectives and identifying the target audience relevant to book publishing can be a potent strategy to adopt. An author with a strong presence on social media not only enhances credibility but also draws in the intended audience.',
      },
      {
        title: 'Kirkus & Foreword Review',
        description: 'Get your book reviewed by one of the most trusted brands in the publishing industry—Kirkus. Find out what the experts think and get your book in front of the Kirkus database with a catalog listing and magazine ad. Foreword book review gives indie authors and publishers more meat on the bone and buzz in the air for publicity campaigns.',
      },
    ],
  },
  {
    id: 'excellence',
    title: 'Pursue Excellence',
    items: [
      {
        title: 'Search Engine Optimization',
        description: 'Search Engine Optimization (SEO) enhances your online visibility, increasing your credibility as an author and creating opportunities for greater reader engagement. Additionally, SEO enables your audience to learn more about you and stay informed about any forthcoming books or events.',
      },
    ],
  },
];

export default function Services() {
  const [activeSection, setActiveSection] = useState('engage');

  const heroAnim = useScrollAnimation({ threshold: 0.15 });
  const accordionAnim = useScrollAnimation({ threshold: 0.1 });
  const browseAnim = useScrollAnimation({ threshold: 0.2 });

  return (
    <main>
      <Seo {...pageSeo.services} />
      {/* Hero Section */}
      <section className="w-full py-12 md:py-16 bg-cream-100 overflow-hidden">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={heroAnim.ref} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 animate-on-scroll ${heroAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex-1">
              <p className="text-xs text-navy-500 uppercase tracking-wider mb-2">Why Leavitt Peak Press?</p>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                We take care of the <span className="text-navy-500">little things</span> for you
              </h1>
              <p className="text-sm text-navy-700 leading-relaxed mb-6">
                Our Leavitt Peak Press publishing book bundles offer different blends of our hybrid publication, and showcasing marketing services for a really customized publishing experience. With Leavitt Peak Press, you can pick the publishing bundle that best suits your book needs.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact-us"
                  className="px-6 py-3 text-sm font-medium text-navy-500 border border-navy-500 rounded-full hover:bg-navy-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Contact Us
                </a>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-[450px] animate-float">
                <img
                  src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Banner-Image-1.png"
                  alt="Publishing Services"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Services Accordion */}
      <section className="w-full py-12 md:py-16 bg-cream-200">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={accordionAnim.ref} className={`animate-on-scroll ${accordionAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-10">
              <p className="text-xs text-navy-500 uppercase tracking-wider mb-2">Our Services</p>
              <h2 className="text-2xl md:text-3xl font-bold text-navy-900">Marketing Services</h2>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {servicesData.map((section, idx) => (
                <div
                  key={section.id}
                  className="bg-white rounded-lg overflow-hidden hover-lift smooth-transition"
                  style={{ animationDelay: `${idx * 0.15}s` }}
                >
                  <button
                    onClick={() => setActiveSection(activeSection === section.id ? '' : section.id)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left group"
                  >
                    <h3 className="text-lg font-semibold text-navy-900 group-hover:text-navy-500 transition-colors duration-300">{section.title}</h3>
                    <span className={`w-8 h-8 flex items-center justify-center rounded-full bg-cream-100 transition-all duration-500 ${activeSection === section.id ? 'bg-navy-500 rotate-180' : 'group-hover:bg-cream-200'}`}>
                      <i
                        className={`transition-colors duration-300 ${activeSection === section.id ? 'text-white' : 'text-navy-900'}`}
                      ></i>
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      activeSection === section.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 space-y-4">
                      {section.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="bg-cream-50 rounded-lg p-5 hover-lift smooth-transition" style={{ animationDelay: `${itemIdx * 0.1}s` }}>
                          <h4 className="text-base font-semibold text-navy-500 mb-2">{item.title}</h4>
                          <p className="text-sm text-navy-700 leading-relaxed whitespace-pre-line">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Browse Collection CTA */}
      <section className="w-full py-12 md:py-16 bg-navy-500 relative overflow-hidden">
        <div className="absolute inset-0 animate-shimmer pointer-events-none"></div>
        <div ref={browseAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${browseAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1">
              <p className="text-xs text-navy-200 uppercase tracking-wider mb-2">Looking for new reads?</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Browse our collection</h2>
              <p className="text-sm text-navy-100 leading-relaxed max-w-2xl">
                Browse through our extensive catalog showcasing the diverse array of published books brought to you by Leavitt Peak Press. Explore this curated collection of literary works encompassing various genres.
              </p>
            </div>
            <a
              href="https://www.amazon.com/s?k=leavitt+peak+press"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 text-sm font-medium text-navy-500 bg-white rounded-full hover:bg-cream-100 transition-all duration-300 hover:scale-105 whitespace-nowrap shrink-0"
            >
              Browse All
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}